import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, UserRound } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { Logo } from "./Logo";
import { NAV, CONTACT } from "../../data/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const { pathname } = useLocation();
  const hasHero = pathname === "/" || pathname.startsWith("/projects/");
  const solid = scrolled || open || !hasHero;
  const { user } = useAuth();
  const account = user ? { to: user.role === "admin" ? "/admin" : "/account", label: user.role === "admin" ? "Admin" : "My account" } : { to: "/login", label: "Sign in" };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow] duration-300 ${solid ? "bg-[#f4f0e8]/95 shadow-[0_1px_0_rgba(0,0,0,0.08)] backdrop-blur-md" : "bg-transparent"}`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 md:px-10">
        <a href="/#home" data-testid="header-logo-link"><Logo light={!solid} /></a>
        <nav className="hidden items-center gap-5 lg:flex xl:gap-7">
          {NAV.map((n) => (
            <a key={n.id} href={`/#${n.id}`} data-testid={`nav-${n.id}-link`} className={`nav-link text-sm font-semibold uppercase tracking-wider ${solid ? "text-[var(--ink)]" : "text-white"}`}>
              {n.label}
            </a>
          ))}
          <Link to={account.to} data-testid="header-account-link" className={`nav-link inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold uppercase tracking-wider ${solid ? "text-[var(--ink)]" : "text-white"}`}>
            <UserRound className="h-4 w-4" /> {account.label}
          </Link>
          <a href={CONTACT.phoneTel} data-testid="header-call-button" className="btn-rust inline-flex items-center gap-2 whitespace-nowrap px-5 py-3 text-sm">
            <Phone className="h-4 w-4" /> Call now
          </a>
        </nav>
        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`lg:hidden ${solid ? "text-[var(--ink)]" : "text-white"}`}
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>
      {open && (
        <nav data-testid="mobile-menu" className="border-t border-black/10 bg-[var(--paper)] px-5 pb-6 lg:hidden">
          {NAV.map((n) => (
            <a key={n.id} href={`/#${n.id}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.id}-link`} className="block border-b border-black/10 py-4 font-display text-2xl font-bold uppercase">
              {n.label}
            </a>
          ))}
          <Link to={account.to} onClick={() => setOpen(false)} data-testid="mobile-account-link" className="block border-b border-black/10 py-4 font-display text-2xl font-bold uppercase">
            {account.label}
          </Link>
          <a href={CONTACT.phoneTel} data-testid="mobile-call-button" className="btn-rust mt-5 flex items-center justify-center gap-2 py-4">
            <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
          </a>
        </nav>
      )}
    </header>
  );
};
